import { useContext, useEffect, useMemo, useState } from "react";
import axios from "../api/httpClient";
import { GlobalState } from "../data/Context";
import { getApiBaseUrl, getStoredLoginEmail, isCompanyAccount } from "../data/authStorage";
import { readSessionCache, writeSessionCache } from "../utils/sessionCache";

const EMPLOYEE_CACHE_PREFIX = "cehr_current_employee_v3";
const employeeMemoryCache = new Map();
const employeeRequestCache = new Map();

const normalizeEmail = (value) => `${value || ""}`.trim().toLowerCase();

const getEmployeeEmails = (employee) =>
  [
    employee?.officialEmail,
    employee?.personalEmail,
    employee?.emailAddress,
    employee?.email,
    employee?.invitedEmail,
  ]
    .map(normalizeEmail)
    .filter(Boolean);

const getEmployeeName = (employee) =>
  employee?.fullName || employee?.name || `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() || "Unnamed employee";

export const getCurrentEmployeeCacheKey = (email) =>
  `${EMPLOYEE_CACHE_PREFIX}_${normalizeEmail(email) || "unknown"}`;

export const updateCurrentEmployeeCache = (email, employee) => {
  const employeeCacheKey = getCurrentEmployeeCacheKey(email);
  employeeMemoryCache.set(employeeCacheKey, employee);
  writeSessionCache(employeeCacheKey, employee);
};

const useCurrentEmployee = () => {
  const { organization } = useContext(GlobalState);
  const loginEmail = useMemo(() => normalizeEmail(getStoredLoginEmail()), []);
  const companyAccount = useMemo(() => isCompanyAccount(organization), [organization]);
  const employeeCacheKey = useMemo(
    () => getCurrentEmployeeCacheKey(loginEmail),
    [loginEmail],
  );
  const cachedEmployee = useMemo(() => {
    if (companyAccount || !loginEmail) {
      return null;
    }

    return employeeMemoryCache.get(employeeCacheKey) || readSessionCache(employeeCacheKey, null);
  }, [companyAccount, employeeCacheKey, loginEmail]);
  const [currentEmployee, setCurrentEmployee] = useState(cachedEmployee);
  const [loading, setLoading] = useState(!companyAccount && !cachedEmployee);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadCurrentEmployee = async () => {
      if (companyAccount || !loginEmail) {
        if (isMounted) {
          setCurrentEmployee(null);
          setLoading(false);
          setError("");
        }
        return;
      }

      const existingEmployee = employeeMemoryCache.get(employeeCacheKey) || readSessionCache(employeeCacheKey, null);

      if (existingEmployee) {
        if (isMounted) {
          setCurrentEmployee(existingEmployee);
          setLoading(false);
          setError("");
        }
        return;
      }

      const apiBaseUrl = getApiBaseUrl();

      if (!apiBaseUrl) {
        if (isMounted) {
          setError("API base URL is not configured.");
          setLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError("");
        }

        const request =
          employeeRequestCache.get(employeeCacheKey) ||
          axios
            .get(`${apiBaseUrl}/organizations/employees/me`)
            .then(async (response) => {
              if (response?.data?.employee?.id) return response.data.employee;
              const employees = Array.isArray(response?.data?.employees) ? response.data.employees : [];
              const matchedEmployee = employees.find((employee) => {
                const employeeEmails = getEmployeeEmails(employee);
                return employeeEmails.includes(loginEmail);
              });

              if (matchedEmployee?.id) {
                try {
                  const detailResponse = await axios.get(
                    `${apiBaseUrl}/organizations/employees/${matchedEmployee.id}`
                  );
                  return detailResponse?.data?.employee || matchedEmployee;
                } catch {
                  return matchedEmployee;
                }
              }

              return matchedEmployee || null;
            })
            .finally(() => {
              employeeRequestCache.delete(employeeCacheKey);
            });

        employeeRequestCache.set(employeeCacheKey, request);
        const matchedEmployee = await request;

        if (matchedEmployee) {
          updateCurrentEmployeeCache(loginEmail, matchedEmployee);
        }

        if (isMounted) {
          setCurrentEmployee(matchedEmployee || null);
        }
      } catch (requestError) {
        if (isMounted) {
          const message = requestError?.response?.data?.message || "Unable to load employee profile.";
          setError(Array.isArray(message) ? message.join(", ") : message);
          setCurrentEmployee(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCurrentEmployee();

    return () => {
      isMounted = false;
    };
  }, [companyAccount, employeeCacheKey, loginEmail]);

  return {
    companyAccount,
    currentEmployee,
    currentEmployeeName: getEmployeeName(currentEmployee),
    currentEmployeeEmail: currentEmployee?.officialEmail || currentEmployee?.personalEmail || currentEmployee?.emailAddress || loginEmail || "",
    currentEmployeePhoto: currentEmployee?.profilePhotoUrl || currentEmployee?.image || "",
    loading,
    error,
    loginEmail,
    modulePermissions: currentEmployee?.modulePermissions || [],
    hasModulePermission: (moduleId) =>
      companyAccount || currentEmployee?.modulePermissions?.includes(moduleId) === true,
  };
};

export default useCurrentEmployee;
